import { useState } from 'react';
import { Button, Input, Select } from '../../components/ui';
import {
  isWeekly,
  plainLabel,
  withFrequency,
  type ScoreField,
  type ScoreSection,
} from './scoreboard';

/**
 * Edit fields as one grouped list: a row per field (handle, name, type, show switch), the
 * details of one field open at a time. Fields are hidden, never deleted.
 */
export function FieldListEditor({
  fields,
  kind,
  addType,
  onAddType,
  onAdd,
  onChange,
  onMove,
  onDelete,
}: {
  fields: ScoreField[];
  kind: ScoreSection['kind'];
  addType: ScoreField['value_type'];
  onAddType: (type: ScoreField['value_type']) => void;
  onAdd: () => string;
  onChange: (id: string, change: Partial<ScoreField>) => void;
  /** Moves the field at `from` to `to`. */
  onMove: (from: number, to: number) => void;
  /** Takes the field out of the list. The database refuses it if the field holds numbers. */
  onDelete: (id: string) => void;
}) {
  const [open, setOpen] = useState<string | null>(null);
  const [dragFrom, setDragFrom] = useState<number | null>(null);
  const raw = fields.filter((f) => f.value_type !== 'rate');
  const typeOf = (field: ScoreField) =>
    field.value_type === 'rate'
      ? `Calculated ${field.rate_format === 'money' ? 'USD' : 'percentage'}`
      : field.value_type === 'money'
        ? 'USD'
        : 'Count';
  return (
    <>
      <ul className="fe-list" aria-label="Fields">
        {fields.map((field, index) => {
          const weekly = isWeekly(field.label);
          const isOpen = open === field.id || plainLabel(field.label).trim() === '';
          const name = plainLabel(field.label).trim() || 'New field';
          const hasFrequency = field.value_type !== 'rate' && kind !== 'snapshot';
          const panel = `fe-${field.id}`;
          return (
            <li
              key={field.id}
              className={`fe-row${isOpen ? ' is-open' : ''}${dragFrom === index ? ' is-dragging' : ''}`}
              onDragOver={(event) => {
                if (dragFrom !== null) event.preventDefault();
              }}
              onDrop={(event) => {
                event.preventDefault();
                if (dragFrom !== null && dragFrom !== index) onMove(dragFrom, index);
                setDragFrom(null);
              }}
            >
              <fieldset className="fe-fieldset">
                <legend className="ui-visually-hidden">
                  {name}
                  {field.is_active ? '' : ' (hidden)'}
                </legend>
                <div className="fe-main">
                  <span
                    className="fe-grip"
                    draggable
                    title="Drag to reorder"
                    aria-hidden="true"
                    onDragStart={(event) => {
                      event.dataTransfer.effectAllowed = 'move';
                      setDragFrom(index);
                    }}
                    onDragEnd={() => setDragFrom(null)}
                  >
                    <svg viewBox="0 0 16 12" focusable="false">
                      <line x1="1" y1="2" x2="15" y2="2" />
                      <line x1="1" y1="6" x2="15" y2="6" />
                      <line x1="1" y1="10" x2="15" y2="10" />
                    </svg>
                  </span>
                  <button
                    type="button"
                    className="fe-name"
                    aria-expanded={isOpen}
                    aria-controls={panel}
                    onClick={() => setOpen(isOpen ? null : field.id)}
                  >
                    <span className={field.is_active ? undefined : 'fe-muted'}>{name}</span>
                    <small>
                      {typeOf(field)}
                      {hasFrequency ? (weekly ? ' · Weekly' : ' · Daily') : ''}
                      {field.is_active ? '' : ' · Hidden'}
                    </small>
                  </button>
                  <button
                    type="button"
                    role="switch"
                    className="fe-switch"
                    aria-checked={field.is_active}
                    aria-label={`Show ${name} on Board and entry`}
                    onClick={() => onChange(field.id, { is_active: !field.is_active })}
                  >
                    <span />
                  </button>
                  <span className="fe-chev" aria-hidden="true">
                    ›
                  </span>
                </div>
                {isOpen ? (
                  <div id={panel} className="fe-detail">
                    <Input
                      label="Field name"
                      value={plainLabel(field.label)}
                      required
                      maxLength={150}
                      onChange={(event) =>
                        onChange(field.id, { label: withFrequency(event.target.value, weekly) })
                      }
                    />
                    {hasFrequency ? (
                      <Select
                        label="Frequency"
                        value={weekly ? 'weekly' : 'daily'}
                        options={[
                          { value: 'daily', label: 'Daily' },
                          { value: 'weekly', label: 'Weekly' },
                        ]}
                        onChange={(event) =>
                          onChange(field.id, {
                            label: withFrequency(field.label, event.target.value === 'weekly'),
                          })
                        }
                      />
                    ) : null}
                    {field.value_type !== 'rate' && kind !== 'snapshot' ? (
                      <Select
                        label="Type"
                        value={field.value_type}
                        options={[
                          { value: 'count', label: 'Count' },
                          { value: 'money', label: 'USD' },
                        ]}
                        onChange={(event) =>
                          onChange(field.id, {
                            value_type: event.target.value as 'count' | 'money',
                            ...(event.target.value === 'money' ? { journey_stage: null } : {}),
                          })
                        }
                      />
                    ) : null}
                    <Input
                      label="Source link"
                      type="url"
                      placeholder="https://"
                      value={field.source_url ?? ''}
                      onChange={(event) =>
                        onChange(field.id, { source_url: event.target.value || null })
                      }
                    />
                    <Input
                      label={
                        field.value_type === 'rate' && field.rate_format === 'percent'
                          ? 'Target (fraction)'
                          : field.value_type === 'money' || field.rate_format === 'money'
                            ? 'Target (USD)'
                            : 'Target'
                      }
                      type="number"
                      min="0"
                      step="any"
                      value={field.target ?? ''}
                      onChange={(event) =>
                        onChange(field.id, {
                          target: event.target.value === '' ? null : Number(event.target.value),
                        })
                      }
                    />
                    {field.value_type === 'count' && kind !== 'snapshot' ? (
                      <Select
                        label="Journey stage"
                        value={field.journey_stage ?? ''}
                        options={[
                          { value: '', label: 'Not mapped' },
                          { value: 'reached', label: 'Reached' },
                          { value: 'engaged', label: 'Engaged' },
                          { value: 'hand', label: 'Raised a hand' },
                          { value: 'call', label: 'Call booked' },
                        ]}
                        onChange={(event) =>
                          onChange(field.id, {
                            journey_stage: (event.target.value || null) as Exclude<
                              ScoreField['journey_stage'],
                              undefined
                            >,
                          })
                        }
                      />
                    ) : null}
                    {field.value_type === 'rate' ? (
                      <>
                        <Select
                          label="Numerator"
                          value={field.numerator_id ?? ''}
                          placeholder="Choose raw field"
                          options={raw.map((f) => ({
                            value: f.id,
                            label: `${f.label}${f.is_active ? '' : ' (hidden)'}`,
                          }))}
                          onChange={(event) =>
                            onChange(field.id, { numerator_id: event.target.value })
                          }
                        />
                        <Select
                          label="Denominator"
                          value={field.denominator_id ?? ''}
                          placeholder="Choose raw field"
                          options={raw.map((f) => ({
                            value: f.id,
                            label: `${f.label}${f.is_active ? '' : ' (hidden)'}`,
                          }))}
                          onChange={(event) =>
                            onChange(field.id, { denominator_id: event.target.value })
                          }
                        />
                        <Select
                          label="Rate display"
                          value={field.rate_format ?? 'percent'}
                          options={[
                            { value: 'percent', label: 'Percentage' },
                            { value: 'money', label: 'USD per unit' },
                          ]}
                          onChange={(event) =>
                            onChange(field.id, {
                              rate_format: event.target.value as 'percent' | 'money',
                            })
                          }
                        />
                      </>
                    ) : null}
                    <div className="fe-move">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="fe-delete"
                        aria-label={`Delete ${name}`}
                        onClick={() => {
                          setOpen(null);
                          onDelete(field.id);
                        }}
                      >
                        Delete
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={index === 0}
                        aria-label={`Move ${name} up`}
                        onClick={() => onMove(index, index - 1)}
                      >
                        Move up
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={index === fields.length - 1}
                        aria-label={`Move ${name} down`}
                        onClick={() => onMove(index, index + 1)}
                      >
                        Move down
                      </Button>
                    </div>
                  </div>
                ) : null}
              </fieldset>
            </li>
          );
        })}
      </ul>
      <p className="fe-foot">
        Drag to reorder. Turn a field off to hide it; its history stays. Weekly fields sit behind
        "Show weekly fields" in Enter numbers and on the right of tables. Delete works for a field
        with no saved numbers. Percent targets use fractions: 0.25 means 25%.
      </p>
      <div className="fe-add">
        <Select
          label="New field type"
          value={addType}
          options={
            kind === 'snapshot'
              ? [{ value: 'count', label: 'Count' }]
              : [
                  { value: 'count', label: 'Count' },
                  { value: 'money', label: 'USD' },
                  { value: 'rate', label: 'Calculated rate' },
                ]
          }
          onChange={(event) => onAddType(event.target.value as ScoreField['value_type'])}
        />
        <Button variant="secondary" onClick={() => setOpen(onAdd())}>
          Add field
        </Button>
      </div>
    </>
  );
}
